<template>
  <div class="login">
    <h1 class="text-blue">{{ $t('auth.welcome') }}</h1>

    <tabs :options="userTypes" v-model="userType"></tabs>
    <form method="post" v-on:submit.prevent="login" name="login">
      <div class="form-group with-icon-left">
        <div class="input-group">
          <input type="email" id="email" v-model="username" required />
          <i class="fa fa-at icon-left input-icon"></i>
          <label class="control-label" for="email">{{
            $t('auth.email')
          }}</label>
          <i class="bar"></i>
        </div>
      </div>
      <div class="form-group with-icon-left">
        <div class="input-group">
          <input type="password" id="password" v-model="password" required />
          <i class="fa fa-user-secret icon-left input-icon"></i>
          <label class="control-label" for="password">{{
            $t('auth.password')
          }}</label>
          <i class="bar"></i>
        </div>
      </div>

      <div class="d-flex align--center justify--center">
        <button
          type="submit"
          class="btn btn-primary btn-sm btn-with-icon-sm btn-block"
        >
          <div class="btn-with-icon-content">
            <i class="fa fa-sign-in"></i>
            {{ $t('auth.login') }}
          </div>
        </button>
      </div>

      <div class="forget-section" v-if="userType === 'user'">
        <router-link class="link-forget" :to="{ name: 'forget-password' }">
          {{ $t('auth.forget-password') }}?
        </router-link>
      </div>
    </form>

    <b-modal
      v-model="expiredModal.open"
      centered
      hide-footer
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      :title="$t('auth.password-expired.title')"
    >
      <p class="mb-3">{{ $t('auth.password-expired.description') }}</p>
      <p v-if="expiredModal.sentOnce" class="text-muted small mb-3">
        {{
          $t('auth.password-expired.check-inbox', {
            email: expiredModal.email
          })
        }}
      </p>
      <div class="d-flex justify-content-end">
        <button
          type="button"
          class="btn btn-secondary btn-sm mr-2"
          @click="closeExpiredModal"
        >
          {{ $t('auth.password-expired.close') }}
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="expiredModal.sending || expiredModal.cooldown > 0"
          @click="sendResetLink"
        >
          <span v-if="expiredModal.cooldown > 0">
            {{
              $t('auth.password-expired.resend-in', {
                seconds: expiredModal.cooldown
              })
            }}
          </span>
          <span v-else-if="expiredModal.sentOnce">
            {{ $t('auth.password-expired.resend') }}
          </span>
          <span v-else>{{ $t('auth.password-expired.send') }}</span>
        </button>
      </div>
    </b-modal>

    <b-modal
      v-model="notVerifiedModal.open"
      centered
      hide-footer
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      :title="$t('auth.not-verified.title')"
    >
      <p class="mb-3">{{ $t('auth.not-verified.description') }}</p>
      <p v-if="notVerifiedModal.sentOnce" class="text-muted small mb-3">
        {{
          $t('auth.not-verified.check-inbox', {
            email: notVerifiedModal.email
          })
        }}
      </p>
      <div class="d-flex justify-content-end">
        <button
          type="button"
          class="btn btn-secondary btn-sm mr-2"
          @click="closeNotVerifiedModal"
        >
          {{ $t('auth.not-verified.close') }}
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="notVerifiedModal.sending || notVerifiedModal.cooldown > 0"
          @click="sendVerificationEmail"
        >
          <span v-if="notVerifiedModal.cooldown > 0">
            {{
              $t('auth.not-verified.resend-in', {
                seconds: notVerifiedModal.cooldown
              })
            }}
          </span>
          <span v-else-if="notVerifiedModal.sentOnce">
            {{ $t('auth.not-verified.resend') }}
          </span>
          <span v-else>{{ $t('auth.not-verified.send') }}</span>
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { api } from '../../../axios'
import Tabs from '../../../elements/Tabs.vue'
import userRoutes from '../../../router/routes/user.routes'
import agentRoutes from '../../../router/routes/agent.routes'

const routes = {
  user: userRoutes,
  agent: agentRoutes
}

export default {
  name: 'login',
  components: { Tabs },
  data() {
    return {
      userType: 'user',
      userTypes: [
        {
          title: 'admin',
          value: 'user'
        },
        {
          title: 'agent',
          value: 'agent'
        }
      ],
      username: '',
      password: '',
      expiredModal: {
        open: false,
        email: '',
        sending: false,
        sentOnce: false,
        cooldown: 0,
        timer: null
      },
      notVerifiedModal: {
        open: false,
        email: '',
        sending: false,
        sentOnce: false,
        cooldown: 0,
        timer: null
      }
    }
  },
  beforeDestroy() {
    this.clearCooldown(this.expiredModal)
    this.clearCooldown(this.notVerifiedModal)
  },
  computed: {
    ...mapGetters(['isLoading'])
  },
  methods: {
    ...mapActions([
      'userLogin',
      'agentLogin',
      'fetchCurrentUser',
      'fetchCurrentAgent'
    ]),
    async login() {
      try {
        const loginFc =
          this.userType === 'user' ? this.userLogin : this.agentLogin
        const res = await loginFc({
          userType: this.userType,
          username: this.username,
          password: this.password
        })

        // Credentials valid but email not verified: prompt to verify instead of
        // logging in (no token was issued).
        if (res && res.notVerified) {
          this.openNotVerifiedModal(res.email || this.username)
          return
        }

        // Credentials valid but the password expired: prompt a reset instead
        // of logging in (no token was issued).
        if (res && res.passwordExpired) {
          this.openExpiredModal(res.email || this.username)
          return
        }

        if (!!res) {
          this.$router.addRoutes(routes[this.userType])
          localStorage.setItem('userType', this.userType)
          switch (this.userType) {
            case 'user':
              this.fetchCurrentUser()
              if (res.organizations.length > 1) {
                location.href = '/'
              } else {
                location.href = '/'
              }
              break
            case 'agent':
              this.fetchCurrentAgent()
              location.href = '/'
              break
          }
        }
      } catch (error) {
        console.log('Login error:', error)
      }
    },
    openExpiredModal(email) {
      this.resetModal(this.expiredModal, email)
    },
    closeExpiredModal() {
      this.clearCooldown(this.expiredModal)
      this.expiredModal.open = false
    },
    async sendResetLink() {
      const modal = this.expiredModal
      if (modal.sending || modal.cooldown > 0) return
      modal.sending = true
      try {
        const response = await api.post(`auth/user/forget-password`, {
          email: modal.email
        })
        const data = response.data || {}
        modal.sentOnce = true
        this.startCooldown(modal, Number(data.retryAfterSeconds) || 180)
        if (data.sent === false) {
          this.toast(this.$t('auth.password-expired.already-sent'), {
            type: 'error'
          })
        } else {
          this.toast(this.$t('auth.password-expired.sent'))
        }
      } catch (e) {
        this.toast(this.$t('auth.password-expired.error'), { type: 'error' })
      }
      modal.sending = false
    },
    openNotVerifiedModal(email) {
      this.resetModal(this.notVerifiedModal, email)
    },
    closeNotVerifiedModal() {
      this.clearCooldown(this.notVerifiedModal)
      this.notVerifiedModal.open = false
    },
    async sendVerificationEmail() {
      const modal = this.notVerifiedModal
      if (modal.sending || modal.cooldown > 0) return
      modal.sending = true
      try {
        const response = await api.post(`v1/register/resend-verification`, {
          email: modal.email
        })
        const data = response.data || {}
        modal.sentOnce = true
        this.startCooldown(modal, Number(data.retryAfterSeconds) || 60)
        if (data.sent === false) {
          this.toast(this.$t('auth.not-verified.already-sent'), {
            type: 'error'
          })
        } else {
          this.toast(this.$t('auth.not-verified.sent'))
        }
      } catch (e) {
        this.toast(this.$t('auth.not-verified.error'), { type: 'error' })
      }
      modal.sending = false
    },
    resetModal(modal, email) {
      this.clearCooldown(modal)
      modal.open = true
      modal.email = email
      modal.sending = false
      modal.sentOnce = false
      modal.cooldown = 0
      modal.timer = null
    },
    startCooldown(modal, seconds) {
      this.clearCooldown(modal)
      modal.cooldown = seconds
      modal.timer = setInterval(() => {
        if (modal.cooldown <= 1) {
          this.clearCooldown(modal)
        } else {
          modal.cooldown -= 1
        }
      }, 1000)
    },
    clearCooldown(modal) {
      if (modal.timer) {
        clearInterval(modal.timer)
        modal.timer = null
      }
      modal.cooldown = 0
    }
  }
}
</script>

<style lang="scss">
.login {
  @include media-breakpoint-down(md) {
    width: 100%;
    padding-right: 2rem;
    padding-left: 2rem;
    .down-container {
      display: none;
    }
  }

  h1 {
    text-align: center;
    margin-top: 0;
    font-weight: bold;
    font-size: 4rem;
    color: #2f8fcf;
  }
  width: 21.375rem;

  .down-container {
    margin-top: 3.125rem;
  }
}
.forget-section {
  text-align: center;
  font-size: 13px;
  margin-top: 20px;
  a {
    color: #495057;
  }
}
</style>
